import { LatLngExpression } from 'leaflet'
import { memo, useEffect, useState } from 'react'
import { Polyline, Tooltip, useMap, useMapEvent } from 'react-leaflet'
import { getPositionName } from 'src/utils/function'
import { IPosition } from '../../../../../generated-types'
import { useMooringsCtx } from 'src/hooks/context-hooks'
import {
  deviationPositions,
  grayedOpacity,
  mapOptions,
  minZoom,
  opacity,
  totalDeviationMultiplier,
} from '../constraints'
import { useShowHiddenPositions } from '../ShowHiddenPositionsContext'
import { getLineHitAreaWeight, midpoint } from '../utils'

interface IFrameLine {
  position: IPosition
  navigateToPosition: (positionId: number) => void
  showCriticalPositions?: boolean
}

const getParams = (position: IPosition) => {
  const latlngs: number[][] = []

  if (position.connectedPositions)
    position.connectedPositions.forEach(e => {
      if (e.positionConnected) {
        latlngs.push([e.latitude, e.longitude])
      }
    })

  const pathOptions = {
    weight: 4,
    opacity: position.isTransparent ? grayedOpacity : opacity,
    color:
      position.deviationsCount > 0
        ? opacity !== 1.0
          ? mapOptions.frameLineColor
          : mapOptions.deviationColor
        : mapOptions.frameLineColor,
    pane: position.isWorkAssignmentPosition ? 'frameLineUpdated' : 'frameLine',
  }

  if (position.deviationsCount != 0) {
    const mid = midpoint(
      position.connectedPositions[0].latitude,
      position.connectedPositions[0].longitude,
      position.connectedPositions[1].latitude,
      position.connectedPositions[1].longitude,
      0.5,
    )
    deviationPositions.push([mid[0], mid[1], totalDeviationMultiplier * position.deviationsCount])
  }
  return { latlngs, pathOptions }
}

export const FrameLine: React.FC<IFrameLine> = memo(
  ({ position, navigateToPosition, showCriticalPositions = false }) => {
    const showHiddenPositions = useShowHiddenPositions()
    const generateFrameLines = true
    const map = useMap()
    const [zoomLevel, setZoomLevel] = useState(Math.round(map.getZoom()))

    useEffect(() => {
      setZoomLevel(Math.floor(map.getZoom()))
    }, [map.getZoom()])

    const handleZoomEnd = event => {
      const newZoomLevel = event.target.getZoom()
      setZoomLevel(Math.round(newZoomLevel))
    }
    useMapEvent('zoomend', handleZoomEnd)
    const { isCritical } = useMooringsCtx()
    const shouldHighlightCritical = !!(isCritical || showCriticalPositions)

    // Hidden positions are drawn greyed out when "show hidden" is on, so they can be found.
    if (position.isHidden && !showHiddenPositions) return <></>
    if (!generateFrameLines || !position.connectedPositions[1]) return <></> //hide if visible set to false.
    if (position.isHidden) position = { ...position, isTransparent: true }

    const { latlngs, pathOptions } = getParams(position)
    const baseWeight = zoomLevel - minZoom <= 0 ? 1 : zoomLevel - minZoom
    pathOptions.weight = position.isCritical && shouldHighlightCritical ? baseWeight + 2 : baseWeight
    const toolTip = mapOptions.lang.frameLine + ' ' + getPositionName(position)

    return (
      <>
        <Polyline
          key={position.id}
          positions={latlngs as LatLngExpression[]}
          pathOptions={pathOptions}
          pane={pathOptions.pane}
          interactive={false}
        />
        <Polyline
          positions={latlngs as LatLngExpression[]}
          pathOptions={{ ...pathOptions, weight: getLineHitAreaWeight(pathOptions.weight), opacity: 0 }}
          pane={pathOptions.pane}
          eventHandlers={{
            click: () => {
              navigateToPosition(position.id)
            },
          }}
        >
          <Tooltip sticky>
            <strong>{toolTip}</strong>
          </Tooltip>
        </Polyline>
      </>
    )
  },
)
